import { useCallback, useEffect, useState } from "react";
import { IoCheckmark } from "react-icons/io5";

import Loading from "../../components/ui/loading";
import { Button } from "../../components/ui/button";
import { TextInput } from "../../components/form/Input";
import SelectInput from "../../components/form/SelectInput";
import { DatePicker } from "../../components/form/DatePicker";
import Alert, { AlertPropsType } from "../../components/ui/alert";
import { FetchDelivertResponse, fetchTodayDeliveryData } from "./ShippingServices";
import { useDispatch } from "react-redux";
import { setSelectedDeliveryData } from "../../redux/slices/deliverySlice";
import ShippingPageLayout from "./ShippingPageLayout";
import ShippingListTable from "./ShippingListTable";

type SelectState = {
  value: string;
  label: string;
};

const CITY_OPTIONS: SelectState[] = [
  { value: "all", label: "Semua Kota" },
  { value: "01", label: "Medan" },
  { value: "03", label: "Duri" },
  { value: "10", label: "Pekanbaru" },
  { value: "11", label: "Kerinci" }
];

const DailyDeliveryReportList = () => {
  const dispatch = useDispatch();

  const [deliveryData, setDeliveryData] = useState<FetchDelivertResponse>();

  // const [selectedOriginCity, setSelectedOriginCity] = useState<SelectState>({ value: "all", label: "Pilih Kota" });
  // const [selectedDestinationCity, setSelectedDestinationCity] = useState<SelectState>({ value: "all", label: "Pilih Kota" });
  const [departureTime, setDepartureTime] = useState<string>("");
  const [selectedShippingDate, setSelectedShippingDate] = useState<Date | undefined>(new Date());
  const [selectedArrivalDate, setSelectedArrivalDate] = useState<Date | undefined>(new Date(new Date().setDate(new Date().getDate() + 1)));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<AlertPropsType | null>(null);

  useEffect(() => {
    // Function to format and set the current time to HH:MM:SS
    const updateTime = () => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');

      setDepartureTime(`${hours} : ${minutes} : ${seconds} WIB`);
    };

    // Run immediately on mount
    updateTime();

    // Update every second
    const intervalId = setInterval(updateTime, 1000);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  const getTodayDeliveryData = useCallback(async () => {
    setIsLoading(true);
    setAlert(null);

    const successRequest = await fetchTodayDeliveryData();
    if (!successRequest) {
      setIsLoading(false);
      setAlert({
        variant: "error",
        message: "Terjadi kesalahan pada server. Mohon coba beberapa saat lagi!"
      });
      return;
    }

    const { status, message, data } = successRequest;

    if (status === 200) {
      setDeliveryData(data);
      dispatch(setSelectedDeliveryData(data));
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setAlert({
        variant: "error",
        message
      });
    };
    return {};
  }, [dispatch]);

  useEffect(() => {
    getTodayDeliveryData();

    return () => { }
  }, [getTodayDeliveryData])

  const submitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Update delivery status");
  };

  if(deliveryData) {
    return (
      <>
        <form onSubmit={submitHandler} className='p-4'>
          <div className='grid lg:grid-cols-2 gap-8'>
            <div>
              <div className='flex flex-col gap-1.5'>
                <label className='font-semibold'>No. Pengiriman</label>
                <TextInput
                  required
                  name="deliveryNumber"
                  value={deliveryData.delivery_number}
                  placeholder="No. Pengiriman"
                  disabled={true}
                  onChange={() => { }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2.5 mt-2">
                <div className='flex flex-col gap-1.5 max-h-10'>
                  <label className="font-semibold">Kota Asal</label>
                  <SelectInput
                    name="origin_city"
                    defaultValue={deliveryData.origin_city}
                    value={deliveryData.origin_city}
                    placeholder="Pilih kota"
                    inputClassName=""
                    disabled={true}
                    options={CITY_OPTIONS}
                    onChange={() => { }}
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <label className="font-semibold">Kota Tujuan</label>
                  <SelectInput
                    name="destination_city"
                    defaultValue={deliveryData.destination_city}
                    value={deliveryData.destination_city}
                    placeholder="Pilih kota"
                    options={CITY_OPTIONS}
                    onChange={() => { }}
                    inputClassName=""
                    disabled={true}
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <label className='font-semibold'>No. Polisi</label>
                  <TextInput
                    required
                    name="platNumber"
                    value={deliveryData.vehicle_plate_number}
                    placeholder="No. Polisi"
                    onChange={() => { }}
                    disabled={true}
                  />
                </div>
                <div className='flex flex-col gap-1.5'>
                  <label className='font-semibold'>Nama Supir</label>
                  <TextInput
                    required
                    name="driverName"
                    value={deliveryData.driver_name}
                    placeholder="Nama Supir"
                    disabled={true}
                    onChange={() => { }}
                  />
                </div>
              </div>
            </div>
            <div>
              <div className='flex flex-col gap-1.5 mt-1.5 lg:mt-0'>
                <label className="font-semibold">Tanggal Pengiriman</label>
                <DatePicker
                  mode="single"
                  value={selectedShippingDate}
                  disabled={true}
                  onChange={() => { }}
                />
              </div>
              <div className='flex flex-col gap-1.5 mt-2.5'>
                <label className='font-semibold'>Jam Berangkat</label>
                <input
                  type="text"
                  id="departureTime"
                  value={departureTime}
                  disabled
                  aria-label="Jam Berangkat"
                  className={`tracking-tight py-2 px-2.5 flex-1 rounded-lg text-sm bg-white border disabled:bg-neutral-300 disabled:text-neutral-700 focus:outline-0 focus-visible:outline-none w-full text-black`}
                />
              </div>
              <div className='flex flex-col gap-1.5 mt-2.5'>
                <label className="font-semibold">Tanggal Tiba</label>
                <DatePicker
                  mode="single"
                  value={selectedArrivalDate}
                  disabled={true}
                  onChange={(value) => setSelectedArrivalDate(value as Date)}
                />
              </div>
            </div>
            {alert &&
              <div className='col-span-2'>
                {<Alert options={alert} />}
              </div>
            }
            <div className='col-span-2 flex justify-end'>
              {isLoading ?
                <div className='py-1'>
                  <Loading show={isLoading} size='small' labelClassName='text-main-blue text-sm' spinnerClassName='text-main-blue text-sm' />
                </div>
                :
                <Button
                  disabled={isLoading}
                  type='submit'
                  className="bg-secondary-blue hover:!bg-sky-400 transition-all ease-in-out duration-300 text-white rounded-xl font-bold text-sm uppercase !py-2"
                >
                  <div className=" flex items-center gap-1.5 px-1">
                    <IoCheckmark size={10} />
                    Konfirmasi Keberangkatan
                  </div>
                </Button>
              }
            </div>
          </div>
        </form>
        <hr className="mt-5"/>
        <div>
          <ShippingPageLayout>
            <ShippingListTable />
          </ShippingPageLayout>
        </div>
      </>
    );
  };

  return (
    <div className="p-0 mt-4 rounded-xl overflow-hidden">
      <div className="bg-neutral-100 text-center py-12">
        <h2 className="text-lg font-bold">Data pengiriman hari ini tidak ditemukan</h2>
        <p className="mt-2">Silahkan masukkan data pengiriman</p>
      </div>
    </div>
  );
}

export default DailyDeliveryReportList